/**
 * WebsiteChatPage — AI chat voor website-aanvragen (FASE 4 — vrije tekst + AI doorvragen).
 *
 * Klant typt in 1 vrij veld wat ze willen. AI stelt door als iets onduidelijk is.
 * Wanneer compleet → omzetten naar formele website-aanvraag.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Send, Sparkles, Loader2, CheckCircle, ArrowRight, MessageCircle, Bot, User,
} from 'lucide-react';

export default function WebsiteChatPage() {
  const { api, user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertedRequestId, setConvertedRequestId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const start = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      const r = await api('post', '/api/platform/website-chat/start', { initial_message: input });
      setSessionId(r.data.session_id);
      setMessages([
        { role: 'user', content: input },
        { role: 'assistant', content: r.data.reply },
      ]);
      setInput('');
      if (r.data.is_complete) {
        setIsComplete(true);
        setExtracted(r.data.extracted);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versturen mislukt');
    }
    setSending(false);
  };

  const send = async () => {
    if (!input.trim() || !sessionId) return;
    setSending(true);
    const userMsg = input;
    setMessages((m) => [...m, { role: 'user', content: userMsg }]);
    setInput('');
    try {
      const r = await api('post', '/api/platform/website-chat/message', { session_id: sessionId, message: userMsg });
      setMessages((m) => [...m, { role: 'assistant', content: r.data.reply }]);
      if (r.data.is_complete) {
        setIsComplete(true);
        setExtracted(r.data.extracted);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versturen mislukt');
      setMessages((m) => m.slice(0, -1));
    }
    setSending(false);
  };

  const convert = async () => {
    if (!sessionId) return;
    setConverting(true);
    try {
      const r = await api('post', `/api/platform/website-chat/${sessionId}/convert`);
      setConvertedRequestId(r.data.request_id);
      toast.success('Aanvraag verzonden — wij nemen binnen 72u contact op');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verzenden mislukt');
    }
    setConverting(false);
  };

  return (
    <div data-testid="website-chat-page" style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <Sparkles size={11} style={{ display: 'inline', marginRight: 4 }} /> AI Website-intake
        </p>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
          Vertel ons wat voor website u wilt
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
          Typ in eigen woorden wat u in gedachten heeft. Onze AI stelt door als er nog iets onduidelijk is — daarna sturen we het naar ons team.
        </p>
      </div>

      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        {/* Chat history */}
        <div ref={scrollRef} data-testid="chat-history" style={{ minHeight: 280, maxHeight: 480, overflowY: 'auto', padding: '16px 18px', background: '#fafbfc' }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              <MessageCircle size={32} style={{ margin: '0 auto 10px', color: '#cbd5e1' }} />
              <p style={{ fontSize: 14 }}>Start het gesprek door hieronder te typen wat voor website u wilt.</p>
              <p style={{ fontSize: 12, marginTop: 6, color: '#cbd5e1' }}>
                bv. <em>"Ik wil een moderne website voor mijn loodgietersbedrijf met online afspraken"</em>
              </p>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}-${i}`} style={{ display: 'flex', gap: 10, marginBottom: 14, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.role === 'user' ? '#D4A017' : '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                  {m.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                </div>
                <div style={{ maxWidth: '78%', background: m.role === 'user' ? '#D4A017' : 'white', color: m.role === 'user' ? '#0f172a' : '#0f172a', padding: '10px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, border: m.role === 'user' ? 'none' : '1px solid #e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {(m.content || '').replace(/\[KLAAR\][\s\S]*$/m, '✅ Alle informatie verzameld — bekijk samenvatting hieronder.').trim()}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <Bot size={14} />
              </div>
              <div style={{ background: 'white', padding: '10px 14px', borderRadius: 12, fontSize: 14, border: '1px solid #e2e8f0', color: '#94a3b8' }}>
                <Loader2 size={14} className="animate-spin" /> Denkt na…
              </div>
            </div>
          )}
        </div>

        {/* Samenvatting + convert (alleen tonen als compleet en nog niet omgezet) */}
        {isComplete && !convertedRequestId && extracted && (
          <div data-testid="chat-summary" style={{ background: '#ecfdf5', padding: '14px 18px', borderTop: '1px solid #d1fae5' }}>
            <p style={{ fontSize: 11, color: '#047857', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={12} /> Samenvatting
            </p>
            <p style={{ fontSize: 13, color: '#0f172a', marginBottom: 6 }}><strong>Type:</strong> {extracted.type_website || 'nieuw'}</p>
            <p style={{ fontSize: 13, color: '#0f172a', marginBottom: 6 }}><strong>Samenvatting:</strong> {extracted.samenvatting}</p>
            {extracted.paginas && extracted.paginas.length > 0 && (
              <p style={{ fontSize: 13, color: '#0f172a', marginBottom: 6 }}><strong>Pagina's:</strong> {extracted.paginas.join(', ')}</p>
            )}
            {extracted.stijl && <p style={{ fontSize: 13, color: '#0f172a', marginBottom: 6 }}><strong>Stijl:</strong> {extracted.stijl}</p>}
            <button onClick={convert} disabled={converting} data-testid="convert-to-request" style={{ marginTop: 8, background: '#047857', color: 'white', fontWeight: 700, padding: '10px 16px', borderRadius: 10, border: 'none', cursor: converting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              {converting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Verstuur naar ons team
            </button>
          </div>
        )}

        {convertedRequestId && (
          <div data-testid="chat-converted" style={{ background: '#f0fdf4', padding: '14px 18px', borderTop: '1px solid #bbf7d0', textAlign: 'center' }}>
            <CheckCircle size={28} color="#16a34a" style={{ margin: '0 auto 8px' }} />
            <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Aanvraag verzonden!</p>
            <p style={{ fontSize: 12, color: '#16a34a', marginBottom: 10 }}>Wij nemen binnen 72 uur contact op.</p>
            <Link to="/platform/website-aanvraag" data-testid="goto-website-aanvraag" style={{ background: '#0f172a', color: 'white', fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Bekijk aanvragen <ArrowRight size={12} />
            </Link>
          </div>
        )}

        {/* Input */}
        {!convertedRequestId && (
          <div style={{ padding: '14px 18px', borderTop: '1px solid #f1f5f9', background: 'white' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (sessionId) send(); else start();
                  }
                }}
                placeholder={sessionId ? 'Antwoord hier…' : 'Beschrijf in eigen woorden welke website u wilt…'}
                rows={2}
                disabled={sending}
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              <button
                data-testid="chat-send"
                onClick={() => sessionId ? send() : start()}
                disabled={sending || !input.trim()}
                style={{ background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '0 18px', borderRadius: 10, border: 'none', cursor: sending ? 'wait' : 'pointer', opacity: (sending || !input.trim()) ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Druk op Enter om te versturen · Shift+Enter voor nieuwe regel</p>
          </div>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 14 }}>
        Liever het standaard formulier? <Link to="/platform/website-aanvraag" style={{ color: '#D4A017', fontWeight: 700 }}>Open hier</Link>
      </p>
    </div>
  );
}
